import { type Appointment, type Client, type Service, formatHourOf, relativeDayLabel, todayIn, utcToZoned } from '@lindeza/core';
import { type AgendaItem, useRuntime } from '@lindeza/core/hooks';
import { toast, toastError, withUndo } from '../../app/ui';
import { IconBellRing, IconTriangleAlert } from '../../components/Icons';

const first = (name?: string) => name?.split(' ')[0] ?? 'Cliente';

/** Choque de horário (2.6): resolver em 1 toque. Nunca apaga sozinho. */
export function ConflictBanner({
  overlaps,
  timezone,
  onReschedule,
}: {
  overlaps: Array<[AgendaItem, AgendaItem]>;
  timezone: string;
  onReschedule: (id: string) => void;
}) {
  const rt = useRuntime();
  return (
    <>
      {overlaps.map(([a, b]) => {
        // Sugere remarcar quem marcou por último (normalmente o do link).
        const other = b.appointment.source === 'link' || a.appointment.source !== 'link' ? b : a;
        return (
          <div key={`${a.appointment.id}-${b.appointment.id}`} className="ag-clash" role="alert">
            <div className="ag-clash-top">
              <IconTriangleAlert />
              <p>
                <strong>Choque de horário:</strong> {first(a.client?.name)} às {formatHourOf(a.appointment.start_utc, timezone)} e {first(b.client?.name)} às{' '}
                {formatHourOf(b.appointment.start_utc, timezone)}.
              </p>
            </div>
            <div className="row">
              <button className="btn btn--primary btn--sm" onClick={() => onReschedule(other.appointment.id)}>
                Remarcar {first(other.client?.name)}
              </button>
              <button className="btn btn--outline btn--sm" onClick={() => rt.actions.keepOverlap([a.appointment.id, b.appointment.id]).catch(toastError)}>
                Manter os dois
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}

/** Pedidos do link aguardando aprovação (3.9): "Beatriz quer horário", Aceitar em 1 toque. */
export function PendingBanner({
  items,
  timezone,
  onOpen,
}: {
  items: Array<{ appointment: Appointment; client: Client | undefined; service: Service | undefined }>;
  timezone: string;
  onOpen: (id: string) => void;
}) {
  const rt = useRuntime();
  const today = todayIn(timezone);
  const firstItem = items[0];
  if (!firstItem) return null;
  const { appointment, client, service } = firstItem;
  const local = utcToZoned(appointment.start_utc, timezone);
  const day = relativeDayLabel(local.date, today);
  const dayLabel = day.includes(',') ? day.split(',')[0]!.replace(/^./, (c) => c.toUpperCase()) : day;
  return (
    <div>
      <div className="ag-req">
        <IconBellRing />
        <button className="ag-req-txt" onClick={() => onOpen(appointment.id)}>
          <b>{first(client?.name)} quer horário</b>
          <span>
            {dayLabel}, {formatHourOf(appointment.start_utc, timezone)}
            <span className="ag-req-svc">, {service?.name.toLowerCase()}</span>
          </span>
        </button>
        <button
          className="btn btn--outline ag-req-refuse"
          onClick={async () => {
            try {
              await rt.actions.cancel(appointment.id);
              withUndo('Pedido recusado.', () => rt.actions.setStatus(appointment.id, 'pending').then(() => undefined));
            } catch (e) {
              toastError(e);
            }
          }}
        >
          Recusar
        </button>
        <button
          className="btn btn--primary"
          onClick={() =>
            void rt.actions
              .accept(appointment.id)
              .then(() => toast(`Horário de ${first(client?.name)} aceito.`))
              .catch(toastError)
          }
        >
          Aceitar
        </button>
      </div>
      {items.length > 1 && (
        <p className="ag-more-req">
          Mais {items.length - 1} {items.length - 1 === 1 ? 'pedido' : 'pedidos'} esperando. Toque no nome para ver.
        </p>
      )}
    </div>
  );
}
